import React, { useEffect, useRef, useState } from 'react';
import { X, ShieldCheck, ExternalLink, Clock, ArrowRight, Mail, MessageCircle, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { money, BRAND, planTermMonths, planTermNote, whatsappHref } from '@/data/constants';
import { track } from '@/lib/tracking';
import { useOverlayLock } from '@/lib/overlay';
import SingleLeadPayment from '@/components/SingleLeadPayment';
import { LeadSettlement, isMobileViewport } from '@/lib/singleLead';

import { PaymentKind } from '@/lib/billing';
import { MarketplaceDisclaimer, AcceptTermsCheckbox } from '@/components/MarketplaceDisclaimer';
import { TERMS_VERSION, BILLING_POLICY_HEADING, CHECKOUT_BILLING_NOTE, bi } from '@/data/legal';


/**
 * Checkout modal.
 *
 * SUBSCRIPTIONS open a secure hosted checkout page; the plan is activated ONLY
 * when the platform receives and validates the payment webhook. Returning from
 * checkout does not activate anything.
 *
 * SINGLE LEADS ($29) are paid inline with Stripe Elements — see
 * SingleLeadPayment. That path did not exist before: with no `planCode` this
 * modal used to fall through to a "See plans" link, which is exactly why the
 * $29 option had no working purchase button.
 *
 * MOBILE: `window.open()` after an await is blocked by Safari/Chrome on phones
 * (the user gesture has already ended), which made the plan Pay button look
 * dead on mobile while working on desktop. openCheckoutUrl() below opens the
 * tab synchronously and falls back to a same-tab navigation.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  amount: number;
  title: string;
  subtitle: string;
  lineItem: string;
  /** Plan code (basic | pro | premium) — required for subscription checkout. */
  planCode?: string;
  kind?: PaymentKind;
  /** Fired once the hosted checkout has been opened (never means "paid"). */
  onCheckoutOpened?: () => void;
  /** Optional alternative action, e.g. use an included subscription lead */
  altAction?: { label: string; run: () => Promise<void> | void };
  /** Optional informational note, e.g. "no included leads left this month" */
  note?: string;
  /** Where to send the user to pick a plan (lead flow with no allowance left). */
  plansHref?: string;
  /** Lead being purchased — enables the inline $29 card form. */
  listingId?: string;
  listingSlug?: string;
  /** Called after a $29 payment settles (credit issued, lead unlocked). */
  onLeadPaid?: (settlement: LeadSettlement) => void;
}

const CheckoutModal: React.FC<Props> = ({
  open,
  onClose,
  amount,
  title,
  subtitle,
  lineItem,
  planCode,
  kind = 'lead',
  onCheckoutOpened,
  altAction,
  note,
  plansHref,
  listingId,
  listingSlug,
  onLeadPaid,
}) => {
  const { t, lang } = useI18n();
  const { user, profile } = useAuth();
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  /** User asked to pay $29 for this specific lead. */
  const [buySingle, setBuySingle] = useState(false);
  const submitting = useRef(false);

  // Register with the overlay registry so the floating Start Now / WhatsApp
  // stack steps aside (and page scroll locks) while checkout is open.
  useOverlayLock(open);

  useEffect(() => {
    if (!open) return;
    setAck(false);
    setError('');
    setSent(false);
    setBuySingle(false);
    submitting.current = false;
  }, [open]);


  /** Store the buyer's acknowledgement of the marketplace disclaimer. */
  const logAck = async () => {
    await supabase.from('mkt_acknowledgements').insert({
      user_id: user?.id || null,
      email: profile?.email || user?.email || null,
      context: kind === 'subscription' ? 'checkout-subscription' : `checkout-${kind}`,
      terms_version: TERMS_VERSION,
      language: lang,
      detail: lineItem,
    });
  };

  /**
   * Record a checkout attempt that could not reach a payment page, so an
   * administrator can follow up (Admin > Payments). The edge function logs this
   * server-side too; `logged` tells us whether that already happened so we do
   * not create a duplicate row.
   */
  const logBlockedAttempt = async (planAttempted: string, reason: string, msg: string) => {
    try {
      await supabase.from('mkt_checkout_failures').insert({
        plan_code: planAttempted,
        user_id: user?.id || profile?.id || null,
        email: profile?.email || user?.email || null,
        name: profile?.company_name || profile?.contact_name || null,
        phone: profile?.phone || null,
        reason,
        message: msg,
        language: lang,
      });
    } catch (_e) {
      /* never block the UI on logging */
    }
  };

  /**
   * MOBILE POPUP FIX. `window.open` is only permitted inside the synchronous
   * part of a tap, so we open the tab immediately (about:blank) and point it at
   * the checkout url once it arrives. If the browser still refuses (iOS Safari
   * with "Block Pop-ups" on returns null), we navigate the current tab instead
   * of silently doing nothing.
   */
  const openCheckoutUrl = (url: string, placeholder: Window | null) => {
    if (placeholder && !placeholder.closed) {
      placeholder.location.href = url;
      return;
    }
    if (isMobileViewport()) {
      window.location.assign(url);
      return;
    }
    const tab = window.open(url, '_blank', 'noopener,noreferrer');
    if (!tab) window.location.assign(url);
  };

  /** Fetch the hosted checkout link for this plan and open it. */
  const goToCheckout = async () => {
    if (!ack || !planCode) return;
    // Synchronous re-entry guard: the first tap wins, repeats are ignored, so
    // repeated taps can never open two checkouts.
    if (submitting.current) return;
    submitting.current = true;

    // Reserve the tab INSIDE the gesture (desktop + Android). Mobile Safari
    // usually returns null here; openCheckoutUrl handles that.
    const placeholder = isMobileViewport() ? null : window.open('', '_blank');

    setBusy(true);
    setError('');
    track('cta_click', { action: 'plan_checkout', plan: planCode });
    await logAck();

    const { data, error: err } = await supabase.functions.invoke('checkout-link', {
      body: {
        plan: planCode,
        email: profile?.email || user?.email || '',
        name: profile?.company_name || profile?.contact_name || '',
        phone: profile?.phone || '',
        userId: user?.id || profile?.id || '',
        language: lang,
      },
    });

    setBusy(false);
    submitting.current = false;

    if (err || !data?.configured || !data?.url) {
      if (placeholder && !placeholder.closed) placeholder.close();
      // The function returns a specific, human readable message when a plan's
      // hosted checkout link has not been set up yet (e.g. "Premium checkout is
      // not configured yet"). Show it verbatim so the button is never a dead end.
      const msg = typeof data?.message === 'string' && data.message ? data.message : t('pay.setup');
      setError(msg);
      if (data?.logged !== true) {
        await logBlockedAttempt(planCode, data?.reason || (err ? 'error' : 'checkout_link_not_set'), msg);
      }
      return;
    }

    openCheckoutUrl(data.url, placeholder);
    setSent(true);
    onCheckoutOpened?.();
  };


  /** Prefilled support email used whenever checkout cannot be started. */
  const contactHref = `mailto:${BRAND.email}?subject=${encodeURIComponent(
    `${planCode ? `${planCode} plan` : 'Checkout'} — ${BRAND.name}`
  )}`;

  /** The $29 option is offered whenever a specific lead is being unlocked. */
  const canBuySingle = kind === 'lead' && !!listingId && !planCode;



  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-900/60 p-0 sm:items-center sm:p-4">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-6 shadow-2xl sm:rounded-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-extrabold text-slate-900">{title}</h2>
            <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100" aria-label={t('common.close')}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-5 rounded-xl bg-slate-50 p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{t('pay.summary')}</p>
          <div className="mt-2 flex items-center justify-between text-sm">
            <span className="text-slate-600">{lineItem}</span>
            <span className="font-semibold text-slate-900">{money(amount)}</span>
          </div>
          <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2 text-base">
            <span className="font-bold text-slate-900">{t('common.total')}</span>
            <span className="font-extrabold text-slate-900">{money(amount)}</span>
          </div>
          {planTermMonths(planCode) > 1 && (
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold leading-relaxed text-amber-900">
              {planTermNote(planCode, amount, lang)}
            </p>
          )}
        </div>


        <p className="mt-4 rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs leading-relaxed text-slate-600">
          <span className="font-bold text-slate-900">{bi(BILLING_POLICY_HEADING, lang)}. </span>
          {bi(CHECKOUT_BILLING_NOTE, lang)}
        </p>

        <MarketplaceDisclaimer variant="short" className="mt-4" />
        <AcceptTermsCheckbox checked={ack} onChange={setAck} kind="checkout" className="mt-3" />


        {altAction && (
          <button
            onClick={async () => {
              if (!ack) return;
              setBusy(true);
              await logAck();
              await altAction.run();
              setBusy(false);
            }}
            disabled={busy || !ack}
            className="mt-4 w-full rounded-xl border-2 border-emerald-600 px-4 py-3 text-sm font-bold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
          >
            {altAction.label}
          </button>
        )}

        {note && <p className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">{note}</p>}

        <div className="mt-5">
          {planCode ? (
            sent ? (
              <div className="rounded-xl bg-blue-50 px-4 py-4 text-sm text-blue-900">
                <p className="flex items-center gap-2 font-bold">
                  <Clock className="h-4 w-4" /> {t('pay.pendingTitle')}
                </p>
                <p className="mt-1.5 leading-relaxed">{t('pay.pendingBody')}</p>
                <button onClick={goToCheckout} className="mt-3 text-sm font-bold text-blue-700 underline">
                  {t('pay.reopenCheckout')}
                </button>
              </div>
            ) : (
              <>
                {error && (
                  <div role="alert" className="mb-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                    <p className="leading-relaxed">{error}</p>
                    <a
                      href={contactHref}
                      className="mt-2 inline-flex items-center gap-1.5 font-bold text-red-800 underline"
                    >
                      <Mail className="h-3.5 w-3.5" /> {t('pay.contactUs')} — {BRAND.email}
                    </a>
                  </div>
                )}

                <button
                  type="button"
                  onClick={goToCheckout}
                  disabled={!ack || busy}
                  style={{ touchAction: 'manipulation' }}
                  className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3.5 text-base font-bold text-white transition-colors hover:bg-orange-600 focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300 disabled:opacity-50"
                >
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                  {busy ? t('pay.processing') : t('pay.continueCheckout')}
                  {!busy && <ExternalLink className="h-4 w-4" />}
                </button>
                {/* Immediate, announced feedback on every tap — desktop AND mobile. */}
                <p aria-live="polite" className="mt-2 min-h-[16px] text-center text-xs font-semibold text-slate-500">
                  {busy ? t('pay.openingCheckout') : ''}
                </p>
                <p className="mt-1 flex items-center justify-center gap-1.5 text-xs text-slate-400">
                  <ShieldCheck className="h-3.5 w-3.5" /> {t('pay.securedBy')}
                </p>
              </>
            )
          ) : canBuySingle ? (
            /*
              $29 SINGLE LEAD CREDIT.
              The payment adds exactly 1 credit to the contractor's balance —
              it does NOT spend it. That is deliberate and matches every
              membership (Basic 5 / Pro 10 / Premium 20 / Single 1): credits
              appear in the balance first and are consumed later, by the
              contractor, with Unlock Lead. The project being viewed is
              remembered only so the confirmation can offer a one-tap unlock.
            */

            buySingle ? (
              <SingleLeadPayment
                listingId={listingId as string}
                slug={listingSlug || ''}
                onSettled={(s) => onLeadPaid?.(s)}
                plansHref={plansHref}
                onLeavePage={onClose}
              />
            ) : (

              <>
                <button
                  type="button"
                  onClick={async () => {
                    if (!ack) return;
                    await logAck();
                    track('cta_click', { action: 'buy_single_lead', listing: listingId });
                    setBuySingle(true);
                  }}
                  disabled={!ack}
                  style={{ touchAction: 'manipulation' }}
                  className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3.5 text-base font-bold text-white transition-colors hover:bg-orange-600 focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300 disabled:opacity-50"
                >
                  {t('pay.buyThisLead')} — {money(amount)}
                </button>
                {/* pay.creditNote, not pay.buyThisLeadNote: the $29 purchase
                    adds 1 credit to the balance and the contractor chooses
                    where to spend it. Nothing unlocks at payment time. */}
                <p className="mt-2 text-center text-xs font-semibold text-slate-500">{t('pay.creditNote')}</p>

                {plansHref && (
                  <Link
                    to={plansHref}
                    onClick={onClose}
                    className="mt-3 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl border-2 border-slate-300 px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
                  >
                    {t('pay.seePlans')} <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </>
            )
          ) : (
            plansHref && (
              <Link
                to={plansHref}
                onClick={onClose}
                className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3.5 text-base font-bold text-white hover:bg-slate-800"
              >
                {t('pay.seePlans')} <ArrowRight className="h-4 w-4" />
              </Link>
            )
          )}
        </div>


        {/* Hesitating at the payment step? One tap to a human on WhatsApp. */}
        <div className="mt-5 flex flex-col items-center gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-between">
          <p className="text-xs font-semibold text-slate-500">{t('cta.questions')}</p>
          <a
            href={whatsappHref(lang)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              track('cta_click', { action: 'whatsapp', channel: 'checkout-modal', plan: planCode || kind })
            }
            className="inline-flex items-center gap-1.5 text-sm font-bold text-[#128C4A] hover:underline"
          >
            <MessageCircle className="h-4 w-4 text-[#25D366]" />
            {t('cta.chatNow')}
          </a>
        </div>

      </div>
    </div>
  );
};

export default CheckoutModal;
